import {
  ALPHABET_SIZE,
  foldDiacritics,
  letterAt,
  letterIndex,
  mapLetters,
  mod,
} from "@/lib/tool-kit/ciphers/alphabet";
import { englishChiSquared } from "@/lib/tool-kit/ciphers/english-frequency";

/**
 * The affine cipher: E(x) = (a·x + b) mod 26, D(y) = a⁻¹·(y − b) mod 26.
 *
 * `a` must share no factor with 26 — otherwise two letters encipher to the same one and
 * the message cannot be read back. That leaves the twelve odd numbers below 26 other
 * than 13, and with 26 choices of `b`, 312 keys. a = 1 is the Caesar cipher, and
 * a = b = 25 is Atbash.
 */

/** Every `a` with an inverse modulo 26. */
export const AFFINE_A_VALUES = [
  1, 3, 5, 7, 9, 11, 15, 17, 19, 21, 23, 25,
] as const;

/** 12 × 26. */
export const AFFINE_KEY_COUNT = AFFINE_A_VALUES.length * ALPHABET_SIZE;

export function gcd(left: number, right: number): number {
  let a = Math.abs(left);
  let b = Math.abs(right);
  while (b !== 0) {
    [a, b] = [b, a % b];
  }
  return a;
}

/** The x in 1…modulus − 1 with a·x ≡ 1 (mod modulus), or null when there is none. */
export function modInverse(a: number, modulus = ALPHABET_SIZE): number | null {
  const value = mod(a, modulus);
  for (let x = 1; x < modulus; x += 1) {
    if ((value * x) % modulus === 1) {
      return x;
    }
  }
  return null;
}

export function isValidAffineA(a: number): boolean {
  return Number.isInteger(a) && gcd(mod(a, ALPHABET_SIZE), ALPHABET_SIZE) === 1;
}

export function affineEncode(text: string, a: number, b: number): string {
  return mapLetters(text, (x) => a * x + b);
}

export function affineDecode(text: string, a: number, b: number): string {
  const inverse = modInverse(a);
  if (inverse === null) {
    throw new RangeError(`a = ${a} has no inverse modulo 26`);
  }
  return mapLetters(text, (y) => inverse * (y - b));
}

export type AffineMode = "encode" | "decode";

export interface AffineStep {
  letter: string;
  /** The letter's number, A = 0. */
  x: number;
  /** Before reduction: a·x + b when encoding, a⁻¹·(y − b) when decoding. */
  raw: number;
  /** `raw` mod 26. */
  value: number;
  result: string;
}

/** The working for the first `limit` letters. `a` must be valid. */
export function affineSteps(
  text: string,
  a: number,
  b: number,
  mode: AffineMode,
  limit: number
): AffineStep[] {
  const inverse = modInverse(a) ?? 1;
  const steps: AffineStep[] = [];
  for (const char of foldDiacritics(text)) {
    if (steps.length >= limit) {
      break;
    }
    const x = letterIndex(char);
    if (x < 0) {
      continue;
    }
    const raw = mode === "encode" ? a * x + b : inverse * (x - b);
    const value = mod(raw, ALPHABET_SIZE);
    steps.push({
      letter: char.toUpperCase(),
      x,
      raw,
      value,
      result: letterAt(value),
    });
  }
  return steps;
}

export interface AffineCandidate {
  a: number;
  b: number;
  text: string;
  /** Chi-squared against English letter frequencies; lower reads more like English. */
  score: number;
}

/**
 * Decode with every one of the 312 keys and rank the results by how English their
 * letter frequencies look, best first. Ties keep key order (a, then b).
 */
export function affineBruteForce(text: string): AffineCandidate[] {
  const candidates: AffineCandidate[] = [];
  for (const a of AFFINE_A_VALUES) {
    for (let b = 0; b < ALPHABET_SIZE; b += 1) {
      const decoded = affineDecode(text, a, b);
      candidates.push({
        a,
        b,
        text: decoded,
        score: englishChiSquared(decoded),
      });
    }
  }
  return candidates.sort((left, right) => left.score - right.score);
}
